import React, { useState, useEffect } from "react";
import { adminClientService } from "../../services/api";
import { Client } from "../../types";
import LoadingSpinner from "../../components/LoadingSpinner";
import Modal from "../../components/Modal";
import ClientDetailModal from "../../components/ClientDetailModal";

const Clients: React.FC = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Modal states
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
    const [clientToApprove, setClientToApprove] = useState<Client | null>(null);
    const [clientToReject, setClientToReject] = useState<Client | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Filter state
    const [filterStatus, setFilterStatus] = useState<
        "all" | "pending" | "approved"
    >("all");

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            setIsLoading(true);
            const data = await adminClientService.getAll();
            setClients(data);
        } catch (err) {
            setError("Erreur lors du chargement des clients");
        } finally {
            setIsLoading(false);
        }
    };

    const openDetailModal = async (client: Client) => {
        try {
            const fullClient = await adminClientService.getById(client.id);
            setSelectedClient(fullClient);
            setIsDetailModalOpen(true);
        } catch (err) {
            setError("Erreur lors du chargement des details");
        }
    };

    const openDeleteModal = (client: Client) => {
        setClientToDelete(client);
        setIsDeleteModalOpen(true);
    };

    const openApproveModal = (client: Client) => {
        setClientToApprove(client);
        setIsApproveModalOpen(true);
    };

    const openRejectModal = (client: Client) => {
        setClientToReject(client);
        setIsRejectModalOpen(true);
    };

    const handleApprove = async () => {
        if (!clientToApprove) return;

        setIsSaving(true);
        try {
            await adminClientService.approve(clientToApprove.id);
            setSuccess("Client approuve avec succes");
            setIsApproveModalOpen(false);
            setClientToApprove(null);
            loadClients();
        } catch (err: any) {
            setError(
                err.response?.data?.error || "Erreur lors de l'approbation",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleReject = async () => {
        if (!clientToReject) return;

        setIsSaving(true);
        try {
            await adminClientService.reject(clientToReject.id);
            setSuccess("Client rejete avec succes");
            setIsRejectModalOpen(false);
            setClientToReject(null);
            loadClients();
        } catch (err: any) {
            setError(err.response?.data?.error || "Erreur lors du rejet");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!clientToDelete) return;

        setIsSaving(true);
        try {
            await adminClientService.delete(clientToDelete.id);
            setSuccess("Client supprime avec succes");
            setIsDeleteModalOpen(false);
            setClientToDelete(null);
            loadClients();
        } catch (err: any) {
            setError(
                err.response?.data?.error || "Erreur lors de la suppression",
            );
        } finally {
            setIsSaving(false);
        }
    };

    // Clear messages after 3 seconds
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess("");
                setError("");
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" text="Chargement des clients..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Gestion des Clients
                    </h1>
                    <p className="text-gray-400">
                        {clients.length} client(s) inscrit(s)
                    </p>
                </div>

                {/* Filters */}
                <div className="mb-6 flex flex-wrap gap-2">
                    <button
                        onClick={() => setFilterStatus("all")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filterStatus === "all"
                                ? "bg-accent text-primary-dark"
                                : "bg-primary-dark/50 text-gray-300 hover:bg-primary-dark"
                        }`}
                    >
                        Tous ({clients.length})
                    </button>
                    <button
                        onClick={() => setFilterStatus("pending")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filterStatus === "pending"
                                ? "bg-yellow-500 text-primary-dark"
                                : "bg-primary-dark/50 text-gray-300 hover:bg-primary-dark"
                        }`}
                    >
                        En attente (
                        {clients.filter((c) => !c.is_approved).length})
                    </button>
                    <button
                        onClick={() => setFilterStatus("approved")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filterStatus === "approved"
                                ? "bg-green-500 text-primary-dark"
                                : "bg-primary-dark/50 text-gray-300 hover:bg-primary-dark"
                        }`}
                    >
                        Approuves ({clients.filter((c) => c.is_approved).length}
                        )
                    </button>
                </div>

                {/* Messages */}
                {error && (
                    <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400">
                        {success}
                    </div>
                )}

                {/* Clients List */}
                {clients.length === 0 ? (
                    <div className="card p-12 text-center">
                        <svg
                            className="w-16 h-16 mx-auto text-gray-600 mb-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                            />
                        </svg>
                        <p className="text-gray-400 text-lg">
                            Aucun client inscrit pour le moment
                        </p>
                    </div>
                ) : (
                    <div className="card overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-primary-dark/50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">
                                        Client
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 hidden md:table-cell">
                                        Date d'inscription
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 hidden sm:table-cell">
                                        Statut
                                    </th>
                                    <th className="px-8 py-4 text-right text-sm font-medium text-gray-300">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10">
                                {clients
                                    .filter((client) => {
                                        if (filterStatus === "pending")
                                            return !client.is_approved;
                                        if (filterStatus === "approved")
                                            return client.is_approved;
                                        return true;
                                    })
                                    .map((client) => (
                                        <tr
                                            key={client.id}
                                            className="hover:bg-primary-dark/30 transition-colors"
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                                                        <span className="text-accent font-semibold">
                                                            {client.username
                                                                .charAt(0)
                                                                .toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium">
                                                            {client.username}
                                                        </p>
                                                        <p className="text-gray-400 text-sm">
                                                            ID: {client.id}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 hidden md:table-cell">
                                                <span className="text-gray-400 text-sm">
                                                    {new Date(
                                                        client.created_at,
                                                    ).toLocaleDateString(
                                                        "fr-FR",
                                                        {
                                                            year: "numeric",
                                                            month: "long",
                                                            day: "numeric",
                                                        },
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 hidden sm:table-cell">
                                                {client.is_approved ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                                                        <svg
                                                            className="w-3 h-3 mr-1"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                        Approuve
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                                                        <svg
                                                            className="w-3 h-3 mr-1"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                            />
                                                        </svg>
                                                        En attente
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() =>
                                                            openDetailModal(
                                                                client,
                                                            )
                                                        }
                                                        className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-colors"
                                                        title="Voir les details"
                                                    >
                                                        <svg
                                                            className="w-5 h-5"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                            />
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                            />
                                                        </svg>
                                                    </button>
                                                    {!client.is_approved && (
                                                        <>
                                                            <button
                                                                onClick={() =>
                                                                    openApproveModal(
                                                                        client,
                                                                    )
                                                                }
                                                                className="p-2 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded-lg transition-colors"
                                                                title="Approuver"
                                                            >
                                                                <svg
                                                                    className="w-5 h-5"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth={
                                                                            2
                                                                        }
                                                                        d="M5 13l4 4L19 7"
                                                                    />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() =>
                                                                    openRejectModal(
                                                                        client,
                                                                    )
                                                                }
                                                                className="p-2 text-orange-400 hover:text-orange-300 hover:bg-orange-500/20 rounded-lg transition-colors"
                                                                title="Rejeter"
                                                            >
                                                                <svg
                                                                    className="w-5 h-5"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth={
                                                                            2
                                                                        }
                                                                        d="M6 18L18 6M6 6l12 12"
                                                                    />
                                                                </svg>
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        onClick={() =>
                                                            openDeleteModal(
                                                                client,
                                                            )
                                                        }
                                                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                                                        title="Supprimer"
                                                    >
                                                        <svg
                                                            className="w-5 h-5"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Client Detail Modal */}
                <ClientDetailModal
                    isOpen={isDetailModalOpen}
                    onClose={() => setIsDetailModalOpen(false)}
                    client={selectedClient}
                    onApprove={openApproveModal}
                    onReject={openRejectModal}
                    onDelete={openDeleteModal}
                />

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    title="Confirmer la suppression"
                    size="sm"
                >
                    <div className="space-y-4">
                        <p className="text-gray-300">
                            Etes-vous sur de vouloir supprimer le client{" "}
                            <span className="text-white font-semibold">
                                {clientToDelete?.username}
                            </span>{" "}
                            ?
                        </p>
                        <p className="text-red-400 text-sm">
                            Cette action est irreversible. Toutes les donnees du
                            client seront supprimees.
                        </p>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="btn-secondary"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isSaving}
                                className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {isSaving ? "Suppression..." : "Supprimer"}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Approve Confirmation Modal */}
                <Modal
                    isOpen={isApproveModalOpen}
                    onClose={() => setIsApproveModalOpen(false)}
                    title="Approuver l'inscription"
                    size="sm"
                >
                    <div className="space-y-4">
                        <p className="text-gray-300">
                            Voulez-vous approuver l'inscription de{" "}
                            <span className="text-white font-semibold">
                                {clientToApprove?.username}
                            </span>{" "}
                            ?
                        </p>
                        <p className="text-green-400 text-sm">
                            Le client pourra acceder a tous les produits et
                            fonctionnalites de la plateforme.
                        </p>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                onClick={() => setIsApproveModalOpen(false)}
                                className="btn-secondary"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleApprove}
                                disabled={isSaving}
                                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {isSaving ? "Approbation..." : "Approuver"}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Reject Confirmation Modal */}
                <Modal
                    isOpen={isRejectModalOpen}
                    onClose={() => setIsRejectModalOpen(false)}
                    title="Rejeter l'inscription"
                    size="sm"
                >
                    <div className="space-y-4">
                        <p className="text-gray-300">
                            Voulez-vous rejeter l'inscription de{" "}
                            <span className="text-white font-semibold">
                                {clientToReject?.username}
                            </span>{" "}
                            ?
                        </p>
                        <p className="text-orange-400 text-sm">
                            Le compte du client sera supprime et il ne pourra
                            pas acceder a la plateforme.
                        </p>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                onClick={() => setIsRejectModalOpen(false)}
                                className="btn-secondary"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={isSaving}
                                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {isSaving ? "Rejet..." : "Rejeter"}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default Clients;
